import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { PageTitleService } from '../../../shared/services';
import { SystemAdminService, CountryDto } from '../../../services/system-admin/system-admin.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../services/auth';
import { Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { DataTable, TableColumn } from '../../../shared/components/data-table/data-table';
import { TableCellDef } from '../../../shared/directives/table-cell.directive';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Modal } from '../../../shared/components/modal/modal';
import { ToastService } from '../../../shared/services';

@Component({
  selector: 'app-countries',
  imports: [DataTable, TableCellDef, FormsModule, ReactiveFormsModule, Modal],
  templateUrl: './countries.html',
  styleUrl: './countries.css',
})
export class Countries implements OnInit {
  private titleService = inject(PageTitleService);
  private systemAdminService = inject(SystemAdminService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private search$ = new Subject<string>();

  countries = signal<CountryDto[]>([]);
  loading = signal(false);
  page = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 1;
  searchTerm = '';
  showModal = signal(false);
  editingItem = signal<CountryDto | null>(null);
  form: FormGroup;

  constructor() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((q) => {
        this.searchTerm = q;
        this.page = 1;
        this.loadCountries();
      });

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      isoCode: ['', [Validators.required, Validators.maxLength(3)]],
      dialCode: ['', [Validators.required, Validators.maxLength(5)]],
      phoneMask: ['', [Validators.required, Validators.maxLength(50)]],
      phoneHolder: ['', [Validators.required, Validators.maxLength(50)]],
    });
  }

  ngOnInit(): void {
    if (!this.authService.isSystemAdmin()) {
      this.router.navigate(['/agendas']);
      return;
    }

    this.titleService.setPage({
      title: 'Países',
      subtitle: 'Gestiona los países disponibles en el sistema',
      breadcrumbs: [{ name: 'Administración', url: '/system-admin' }],
    });
    this.loadCountries();
  }

  onSearch = (q: string) => this.search$.next(q);

  loadCountries(): void {
    this.loading.set(true);
    this.systemAdminService.getCountries({
      page: this.page,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm || undefined,
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.countries.set(response.countries);
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading countries:', err);
        this.loading.set(false);
      }
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadCountries();
  }

  openCreateModal(): void {
    this.editingItem.set(null);
    this.form.reset();
    this.showModal.set(true);
  }

  openEditModal(item: CountryDto): void {
    this.editingItem.set(item);
    this.form.patchValue({
      name: item.name,
      isoCode: item.isoCode,
      dialCode: item.dialCode,
      phoneMask: item.phoneMask,
      phoneHolder: item.phoneHolder,
    });
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingItem.set(null);
    this.form.reset();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    const item = this.editingItem();

    if (item) {
      this.systemAdminService.updateCountry(item.countryId, {
        name: value.name,
        isoCode: value.isoCode,
        dialCode: value.dialCode,
        phoneMask: value.phoneMask,
        phoneHolder: value.phoneHolder,
      }).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.toastService.show('País actualizado exitosamente', 'success');
          this.closeModal();
          this.loadCountries();
        },
        error: (err) => {
          this.toastService.show(err.error?.message || 'Error al actualizar país', 'error');
        }
      });
    } else {
      this.systemAdminService.createCountry({
        name: value.name,
        isoCode: value.isoCode,
        dialCode: value.dialCode,
        phoneMask: value.phoneMask,
        phoneHolder: value.phoneHolder,
      }).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.toastService.show('País creado exitosamente', 'success');
          this.closeModal();
          this.loadCountries();
        },
        error: (err) => {
          this.toastService.show(err.error?.message || 'Error al crear país', 'error');
        }
      });
    }
  }

  delete(item: CountryDto): void {
    if (!confirm(`¿Estás seguro de eliminar "${item.name}"?`)) {
      return;
    }

    this.systemAdminService.deleteCountry(item.countryId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toastService.show('País eliminado exitosamente', 'success');
        this.loadCountries();
      },
      error: (err) => {
        this.toastService.show(err.error?.message || 'Error al eliminar país', 'error');
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/system-admin']);
  }

  columns: TableColumn[] = [
    {
      header: 'Nombre',
      field: 'name',
    },
    {
      header: 'Código ISO',
      field: 'isoCode',
    },
    {
      header: 'Código de Marcado',
      field: 'dialCode',
    },
    {
      header: 'Máscara',
      field: 'phoneMask',
    },
    {
      header: 'Fecha Creación',
      field: 'createdAt',
      format: (v) => formatDate(v, 'dd/MM/yyyy', 'es-CO'),
    },
    {
      header: 'Acciones',
      cell: 'acciones',
      widthClass: 'w-40',
    },
  ];
}

